import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { usePopup } from "../../../contexts/PopupContext";
import ShareModal from "../ShareModal";
import SharedWithMeSection from "../SharedWithMeSection";
import {
  Plus,
  Trash2,
  Star,
  Waypoints,
  Globe,
  Link2,
  Pencil,
  Share2,
} from "lucide-react";

import {
  getGraphViews,
  getTeamGraphs,
  deleteGraphView,
  unfavoriteGraphView,
  saveGraphViewOrder,
} from "../../../../api/graph";
import {
  usePortalTarget,
  feedViewIcon,
  sidebarIcon,
  sidebarTypeColor,
} from "./sidebarUtils";
import { useLayout } from "../../../contexts/LayoutContext";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const getTeamMemberUuid = (member) =>
  member?.uuid ||
  member?.user_uuid ||
  member?.userUuid ||
  member?.team_uuid ||
  member?.teamUuid ||
  member?.document_id ||
  member?.id;

const getTeamMemberName = (member) => {
  const directName =
    member?.name || member?.display_name || member?.displayName;
  if (directName) return directName;

  const first = member?.first_name || member?.firstName || "";
  const last = member?.last_name || member?.lastName || "";
  const fullName = `${first} ${last}`.trim();

  return fullName || member?.email || "Team Member";
};

const GraphSidebarPortal = () => {
  const { currentAppPage, leftOpen } = useLayout();
  const [shareTarget, setShareTarget] = useState(null);
  const portalTarget = usePortalTarget("graph-portal-slot");
  const navigate = useNavigate();
  const [views, setViews] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [viewOrder, setViewOrder] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingGraphDeleteId, setPendingGraphDeleteId] = useState(null);
  const [activeSource, setActiveSource] = useState({ kind: "my_graph" });
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();

  // Clear active state when another section becomes active
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "graph") {
        setActiveSource({ kind: null });
      }
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  // Drag state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await getGraphViews();
      if (res?.data?.views) setViews(res.data.views);
      if (res?.data?.favorites) setFavorites(res.data.favorites);
      if (res?.data?.view_order) setViewOrder(res.data.view_order);
    } catch (e) {
      console.error("Error loading graph views:", e);
    }
  }, []);

  const loadTeamMembers = useCallback(async () => {
    try {
      const res = await getTeamGraphs();
      if (res?.data) {
        setTeamMembers(
          Array.isArray(res.data)
            ? res.data
            : res.data.team || res.data.members || [],
        );
      }
    } catch (e) {
      console.error("Error loading team graphs:", e);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadTeamMembers();
  }, [loadData, loadTeamMembers]);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener("sidebar:refresh-graph-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-graph-views", handler);
  }, [loadData]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setActiveSource(e.detail);
    };
    window.addEventListener("graph:active-source-changed", handler);
    return () =>
      window.removeEventListener("graph:active-source-changed", handler);
  }, []);

  /* ── Breadcrumb: emit active sub-item ── */
  const emitGraphBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/graph") return;
    let label = null;
    if (activeSource.kind === "my_graph") label = "My Graph";
    else if (activeSource.kind === "global") label = "Global Graph";
    else if (activeSource.label) label = activeSource.label;
    else if (activeSource.kind === "custom_view" && activeSource.view)
      label = activeSource.view.name;
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/graph",
          subLabel: label,
          subIcon: null,
          hasAdd: true,
          addKind: "graph",
          addLabel: "Add graph view",
        },
      }),
    );
  }, [activeSource, currentAppPage]);

  useEffect(() => {
    emitGraphBreadcrumb();
  }, [emitGraphBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/graph") emitGraphBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitGraphBreadcrumb]);

  /* ── Breadcrumb: listen for add graph-view action ── */
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      const isGraphAdd =
        detail.section === "/graph" &&
        (detail.addKind === "graph" ||
          detail.kind === "graph" ||
          detail.type === "graph");

      if (!isGraphAdd) return;

      if (currentAppPage !== "/graph") navigate("/graph");

      window.dispatchEvent(
        new CustomEvent("sidebar:active-section-changed", {
          detail: { section: "graph" },
        }),
      );

      const fire = () => {
        window.dispatchEvent(
          new CustomEvent("sidebar:graph-open-create-modal"),
        );

        // Backward-compatible fallback for graph pages that reuse the edit modal
        // component and branch on mode/view presence.
        window.dispatchEvent(
          new CustomEvent("sidebar:graph-open-edit-modal", {
            detail: { mode: "create", view: null },
          }),
        );
      };

      fire();
      setTimeout(fire, 50);
      setTimeout(fire, 200);
    };

    window.addEventListener("breadcrumb:add", handler);
    window.addEventListener("breadcrumb:add-click", handler);
    window.addEventListener("breadcrumb:add-action", handler);
    window.addEventListener("breadcrumb:action", handler);

    return () => {
      window.removeEventListener("breadcrumb:add", handler);
      window.removeEventListener("breadcrumb:add-click", handler);
      window.removeEventListener("breadcrumb:add-action", handler);
      window.removeEventListener("breadcrumb:action", handler);
    };
  }, [currentAppPage, navigate]);

  /* ── Build unified ordered list ── */
  const orderedItems = useMemo(() => {
    const itemMap = new Map();

    // "My Graph" is always first
    itemMap.set("builtin:my_graph", {
      orderId: "builtin:my_graph",
      kind: "builtin",
      data: { key: "my_graph", label: "My Graph" },
    });

    // Global graph
    itemMap.set("builtin:global", {
      orderId: "builtin:global",
      kind: "builtin_global",
      data: { key: "global", label: "Global" },
    });

    // Team members
    teamMembers.forEach((m) => {
      const teamUuid = getTeamMemberUuid(m);
      if (!teamUuid) return;

      const id = `team:${teamUuid}`;
      itemMap.set(id, { orderId: id, kind: "team", data: m });
    });

    favorites.forEach((f) => {
      const id = `fav:${f.document_id}`;
      itemMap.set(id, { orderId: id, kind: "favorite", data: f });
    });

    views.forEach((v) => {
      const id = `custom:${v.document_id}`;
      itemMap.set(id, { orderId: id, kind: "custom", data: v });
    });

    if (viewOrder.length > 0) {
      const result = [];
      const used = new Set();
      viewOrder.forEach((id) => {
        if (itemMap.has(id)) {
          result.push(itemMap.get(id));
          used.add(id);
        }
      });
      itemMap.forEach((item, id) => {
        if (!used.has(id)) result.push(item);
      });
      return result;
    }

    return [...itemMap.values()];
  }, [views, favorites, viewOrder, teamMembers]);

  /* ── Search registry ── */
  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    register(
      "/graph",
      orderedItems.map((item) => {
        if (item.kind === "builtin") {
          return {
            id: item.orderId,
            label: "My Graph",
            keywords: ["graph", "network"],
            icon: Waypoints,
            iconColor: "rgba(14,165,233,0.70)",
            event: {
              navigateTo: "/graph",
              name: "sidebar:graph-switch-source",
              detail: { kind: "my_graph" },
            },
          };
        }
        if (item.kind === "builtin_global") {
          return {
            id: item.orderId,
            label: "Global Graph",
            keywords: ["graph", "global", "all", "public"],
            icon: Globe,
            iconColor: "rgba(6,182,212,0.70)",
            event: {
              navigateTo: "/graph",
              name: "sidebar:graph-switch-source",
              detail: { kind: "global" },
            },
          };
        }
        if (item.kind === "team") {
          const m = item.data;
          const teamUuid = getTeamMemberUuid(m);
          const teamName = getTeamMemberName(m);

          return {
            id: item.orderId,
            label: `${teamName}'s Graph`,
            keywords: [
              "graph",
              "team",
              teamName,
              m.first_name,
              m.last_name,
              m.email,
            ].filter(Boolean),
            icon: Waypoints,
            iconColor: "rgba(14,165,233,0.70)",
            event: {
              navigateTo: "/graph",
              name: "sidebar:graph-switch-source",
              detail: {
                kind: "team",
                uuid: teamUuid,
                label: `${teamName}'s Graph`,
                meta: {
                  team_uuid: teamUuid,
                  team_name: teamName,
                },
              },
            },
          };
        }
        if (item.kind === "favorite") {
          const f = item.data;
          return {
            id: item.orderId,
            label: f.favorite_label,
            keywords: ["graph", "favorite"],
            icon: sidebarIcon(f.favorite_value),
            iconColor:
              f.favorite_kind === "node_type"
                ? sidebarTypeColor(f.favorite_value)
                : "rgba(14,165,233,0.70)",
            event: {
              navigateTo: "/graph",
              name: "sidebar:graph-switch-source",
              detail: {
                kind: f.favorite_kind,
                value: f.favorite_value,
                label: f.favorite_label,
                meta: f.favorite_meta || {},
              },
            },
          };
        }
        if (item.kind === "custom") {
          const v = item.data;
          return {
            id: item.orderId,
            label: v.name,
            keywords: ["graph", "view", "custom"],
            icon: feedViewIcon(v.icon),
            iconColor: v.color || "rgba(148,163,184,0.60)",
            event: {
              navigateTo: "/graph",
              name: "sidebar:graph-switch-source",
              detail: { kind: "custom_view", view: v },
            },
          };
        }
        return { id: item.orderId, label: "Unknown", keywords: [] };
      }),
    );
    return () => unregister("/graph");
  }, [orderedItems, register, unregister]);

  const goToGraph = () => {
    if (currentAppPage !== "/graph") {
      navigate("/graph");
      navigate("/graph");
    }
  };

  // Dispatch the source-switch event with retries so MainGraph receives it
  // even if it hasn't mounted yet after a route change.
  const dispatchSourceSwitch = useCallback((detail) => {
    // Set sidebar highlight immediately — don't wait for MainGraph echo.
    setActiveSource(detail);

    // Persist the requested switch for the graph page. This covers the case
    // where the sidebar click navigates to /graph and MainGraph has not mounted
    // by the time the first event fires.
    try {
      window.__pendingGraphSourceSwitch = detail;
      sessionStorage.setItem(
        "pendingGraphSourceSwitch",
        JSON.stringify(detail),
      );
    } catch {}

    const fire = () =>
      window.dispatchEvent(
        new CustomEvent("sidebar:graph-switch-source", { detail }),
      );

    // Fire immediately for the already-mounted /graph page, then retry for
    // route-transition timing. This keeps existing behavior but removes the
    // short window where the event can be missed entirely.
    fire();
    setTimeout(fire, 0);
    setTimeout(fire, 50);
    setTimeout(fire, 200);
    setTimeout(fire, 500);
    setTimeout(fire, 1000);
  }, []);

  const handleMyGraph = () => {
    goToGraph();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "graph" },
      }),
    );
    dispatchSourceSwitch({ kind: "my_graph" });
  };

  const handleGlobalGraph = () => {
    goToGraph();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "graph" },
      }),
    );
    dispatchSourceSwitch({ kind: "global" });
  };

  const handleTeamMemberClick = (member) => {
    const teamUuid = getTeamMemberUuid(member);
    const teamName = getTeamMemberName(member);

    if (!teamUuid) {
      console.warn("[GraphSidebarPortal] Missing team uuid", member);
      return;
    }

    goToGraph();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "graph" },
      }),
    );
    dispatchSourceSwitch({
      kind: "team",
      uuid: teamUuid,
      label: `${teamName}'s Graph`,
      meta: {
        team_uuid: teamUuid,
        team_name: teamName,
      },
    });
  };

  const handleFavoriteClick = (fav) => {
    goToGraph();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "graph" },
      }),
    );
    dispatchSourceSwitch({
      kind: fav.favorite_kind,
      value: fav.favorite_value,
      label: fav.favorite_label,
      meta: fav.favorite_meta || {},
    });
  };

  const handleUnfavorite = async (e, fav) => {
    e.stopPropagation();
    try {
      await unfavoriteGraphView({ document_id: fav.document_id });
      loadData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
    } catch (err) {
      console.error("Unfavorite failed:", err);
    }
  };

  const handleViewClick = (view) => {
    goToGraph();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "graph" },
      }),
    );
    dispatchSourceSwitch({ kind: "custom_view", view });
  };

  const handleDeleteView = async (e, view) => {
    e.stopPropagation();
    try {
      await deleteGraphView({ document_id: view.document_id });
      loadData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
    } catch (err) {
      console.error("Delete view failed:", err);
    }
  };

  const handleAddView = () => {
    goToGraph();
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "graph" },
      }),
    );

    const fire = () => {
      window.dispatchEvent(new CustomEvent("sidebar:graph-open-create-modal"));

      // Backward-compatible fallback for graph pages that reuse the edit modal
      // component and branch on mode/view presence.
      window.dispatchEvent(
        new CustomEvent("sidebar:graph-open-edit-modal", {
          detail: { mode: "create", view: null },
        }),
      );
    };

    fire();
    setTimeout(fire, 50);
    setTimeout(fire, 200);
  };

  const handleEditView = (e, view) => {
    e.stopPropagation();
    goToGraph();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("sidebar:graph-open-edit-modal", { detail: { view } }),
      );
    }, 50);
  };

  /* ── Context menu registrations ── */
  const confirmDeleteGraphView = async (documentId) => {
    try {
      await deleteGraphView({ document_id: documentId });
      loadData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
    } catch (err) {
      console.error("Delete view failed:", err);
    } finally {
      setPendingGraphDeleteId(null);
    }
  };

  useContextMenu("graph-custom-view", [
    {
      label: "Share",
      icon: Share2,
      action: (payload) => {
        setPopUpComponent(
          <ShareModal
            onClose={() => setIsPopUpOpen(false)}
            contentType="custom_graph_view"
            documentId={payload.document_id}
            itemName={payload.name}
          />,
        );
        setIsPopUpOpen(true);
      },
    },
    {
      label: "Edit View",
      icon: Pencil,
      action: (payload) => {
        goToGraph();
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("sidebar:graph-open-edit-modal", {
              detail: { view: payload },
            }),
          );
        }, 50);
      },
    },
    {
      label: "Delete View",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        setPendingGraphDeleteId(payload.document_id);
      },
    },
  ]);

  useContextMenu("graph-favorite", [
    {
      label: "Remove Favorite",
      icon: Star,
      variant: "danger",
      action: async (payload) => {
        try {
          await unfavoriteGraphView({ document_id: payload.document_id });
          loadData();
          window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
        } catch (err) {
          console.error("Unfavorite failed:", err);
        }
      },
    },
  ]);

  /* ── Drag and drop ── */
  const handleDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    if (e.currentTarget) {
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const handleDragLeave = () => {
    setDragOverIdx(null);
  };

  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null);
      setDragOverIdx(null);
      return;
    }

    const items = [...orderedItems];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(dropIdx, 0, moved);

    const newOrder = items.map((item) => item.orderId);
    setViewOrder(newOrder);
    setDragIdx(null);
    setDragOverIdx(null);

    try {
      await saveGraphViewOrder({ order: newOrder });
    } catch (err) {
      console.error("Save graph order failed:", err);
    }
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  /* ── Row style helpers ── */
  const rowClasses = (isActive) =>
    [
      "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-all duration-200 flex items-center gap-x-1.5 group/row",
      isActive
        ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
        : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
    ].join(" ");

  /* ── Render a single item row ── */
  const renderItem = (item, idx) => {
    const isDragging = dragIdx === idx;
    const isDragOver = dragOverIdx === idx;

    const dragProps = {
      draggable: true,
      onDragStart: (e) => handleDragStart(e, idx),
      onDragOver: (e) => handleDragOver(e, idx),
      onDragLeave: handleDragLeave,
      onDrop: (e) => handleDrop(e, idx),
      onDragEnd: handleDragEnd,
    };

    const dragStyle = {
      opacity: isDragging ? 0.35 : 1,
      borderTop:
        isDragOver && dragIdx !== null && dragIdx > idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      borderBottom:
        isDragOver && dragIdx !== null && dragIdx < idx
          ? "2px solid rgba(14,165,233,0.5)"
          : undefined,
      cursor: "grab",
    };

    // ── Built-in: My Graph ──
    if (item.kind === "builtin") {
      const isActive = activeSource.kind === "my_graph";
      return (
        <div key={item.orderId} style={dragStyle} {...dragProps}>
          <button onClick={handleMyGraph} className={rowClasses(isActive)}>
            <Waypoints
              className="h-3 w-3 shrink-0 text-[#0EA5E9]/70"
              strokeWidth={2}
            />
            <span className="truncate">My Graph</span>
          </button>
        </div>
      );
    }

    // ── Built-in: Global ──
    if (item.kind === "builtin_global") {
      const isActive = activeSource.kind === "global";
      return (
        <div key={item.orderId} style={dragStyle} {...dragProps}>
          <button onClick={handleGlobalGraph} className={rowClasses(isActive)}>
            <Globe
              className="h-3 w-3 shrink-0 text-cyan-500/70"
              strokeWidth={2}
            />
            <span className="truncate">Global Graph</span>
          </button>
        </div>
      );
    }

    // ── Team member ──
    if (item.kind === "team") {
      const m = item.data;
      const teamUuid = getTeamMemberUuid(m);
      const teamName = getTeamMemberName(m);
      const isActive =
        activeSource.kind === "team" &&
        (activeSource.uuid === teamUuid ||
          activeSource.meta?.team_uuid === teamUuid);

      return (
        <div key={item.orderId} style={dragStyle} {...dragProps}>
          <button
            onClick={() => handleTeamMemberClick(m)}
            className={rowClasses(isActive)}
          >
            <div className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-[#0EA5E9]/10 text-[7px] font-semibold text-[#0EA5E9]/70">
              {(teamName?.[0] || "?").toUpperCase()}
            </div>
            <span className="truncate">{teamName}&apos;s Graph</span>
          </button>
        </div>
      );
    }

    // ── Favorite ──
    if (item.kind === "favorite") {
      const fav = item.data;
      const FavIcon =
        fav.favorite_kind === "global"
          ? Globe
          : fav.favorite_kind === "team"
            ? Waypoints
            : fav.favorite_kind === "edge_type"
              ? Link2
              : fav.favorite_kind === "node_type"
                ? sidebarIcon(fav.favorite_value)
                : Waypoints;

      const isActive =
        (fav.favorite_kind === "global" && activeSource.kind === "global") ||
        (fav.favorite_kind === "team" &&
          activeSource.kind === "team" &&
          activeSource.uuid === fav.favorite_meta?.team_uuid) ||
        (fav.favorite_kind === "node_type" &&
          (activeSource.kind === "node" || activeSource.kind === "node_type") &&
          activeSource.value === fav.favorite_value) ||
        (fav.favorite_kind === "edge_type" &&
          (activeSource.kind === "link" || activeSource.kind === "edge_type") &&
          activeSource.value === fav.favorite_value);

      const favIconStyle =
        fav.favorite_kind === "node_type"
          ? { color: sidebarTypeColor(fav.favorite_value) }
          : {};
      const iconClass =
        fav.favorite_kind === "global"
          ? "text-cyan-500/70"
          : fav.favorite_kind === "team"
            ? "text-[#0EA5E9]/70"
            : fav.favorite_kind === "edge_type"
              ? "text-slate-400 dark:text-gray-500"
              : "";

      return (
        <div
          key={item.orderId}
          style={dragStyle}
          {...dragProps}
          data-context-menu="graph-favorite"
          data-context-payload={JSON.stringify(fav)}
        >
          <button
            onClick={() => handleFavoriteClick(fav)}
            className={rowClasses(isActive)}
          >
            <FavIcon
              className={`h-3 w-3 shrink-0 ${iconClass}`}
              strokeWidth={2}
              style={favIconStyle}
            />
            <span className="truncate flex-1">{fav.favorite_label}</span>
            <Star
              className="h-3 w-3 shrink-0 text-amber-400 opacity-60"
              strokeWidth={2}
              fill="currentColor"
            />
          </button>
        </div>
      );
    }

    // ── Custom view ──
    if (item.kind === "custom") {
      const view = item.data;
      const isActive =
        activeSource.kind === "custom_view" &&
        activeSource.viewId === view.document_id;
      const ViewIcon = feedViewIcon(view.icon);
      const isPendingDelete = pendingGraphDeleteId === view.document_id;
      return (
        <div
          key={item.orderId}
          style={dragStyle}
          {...dragProps}
          data-context-menu="graph-custom-view"
          data-context-payload={JSON.stringify(view)}
        >
          <button
            onClick={() => handleViewClick(view)}
            className={rowClasses(isActive)}
          >
            <ViewIcon
              className="h-3 w-3 shrink-0"
              strokeWidth={2}
              style={{ color: view.color || "rgba(148,163,184,0.60)" }}
            />
            <span className="truncate flex-1">{view.name}</span>
          </button>

          {/* ── Inline delete confirmation ── */}
          <div
            className={[
              "overflow-hidden transition-all duration-200 ease-in-out",
              isPendingDelete
                ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
                : "max-h-0 opacity-0",
            ].join(" ")}
          >
            {isPendingDelete && (
              <div className="flex items-center gap-x-2 py-1 px-1">
                <span className="text-[11px] text-slate-500 dark:text-gray-400">
                  Delete?
                </span>
                <button
                  onClick={() => setPendingGraphDeleteId(null)}
                  className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDeleteGraphView(view.document_id)}
                  className="text-[11px] px-2 py-0.5 rounded border border-red-300 dark:border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                >
                  Confirm
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  const content = (
    <div className="flex flex-col gap-y-px">
      {/* ── + Add graph view; hidden in navbar because breadcrumb owns the action there ── */}
      {!isInNavbar && (
        <button
          onClick={handleAddView}
          className="mb-1 flex w-full items-center gap-x-1.5 rounded-md px-2 py-1.5 text-left text-[12px] text-zinc-500 transition-all duration-200 hover:bg-zinc-50 hover:text-zinc-700 dark:text-zinc-500 dark:hover:bg-zinc-800/40 dark:hover:text-zinc-300"
        >
          <Plus
            className="h-3 w-3 shrink-0 text-[#0EA5E9]/70"
            strokeWidth={2}
          />
          <span className="truncate">Add graph view</span>
        </button>
      )}

      {/* ── Ordered items (drag & drop) ── */}
      {orderedItems.map((item, idx) => renderItem(item, idx))}
      <SharedWithMeSection
        contentType="graphs"
        onSelect={(item) => handleViewClick(item)}
      />
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default GraphSidebarPortal;
